// External
import { useMemo, useCallback } from "react";
import _ from "lodash";

// Internal
import {
  ROW_SECTION_ID_TO_CONFIG_KEY,
  ROW_SECTION_IDS_WITHOUT_CHILDREN,
  ROW_ITEM_KEYS,
} from "../../../modules/constants";
import {
  getGradeScaleTypeMap,
  getGradeScaleLabelMap,
  getRowChildTypes,
  getRowItemMetaById,
  getParentCheckState,
  buildRowChildren,
  getEnabledGradeScaleIds,
  getLabelValue,
  getNormalizedYearAggregateItemId,
} from "../../../modules/utils";

// Relative
import {
  getResolvedLabelForChildren,
  getDescriptionByItemId,
  getYearAggregateDescription,
} from "../modules/viewModelHelpers";

export const useRowViewModel = ({
  configuredRowSections,
  transcriptConfiguration,
  language,
  t,
}) => {
  const createResolvedChildLabelFn = useCallback(
    () => label => getResolvedLabelForChildren({ label, language, t }),
    [language, t]
  );

  const rowSectionViewModel = useMemo(() => {
    const gradeScaleTypeMap = getGradeScaleTypeMap({
      transcriptConfiguration,
    });
    const gradeScaleLabelMap = getGradeScaleLabelMap({
      transcriptConfiguration,
    });

    const resolvedChildLabelFn = createResolvedChildLabelFn();

    return _.map(configuredRowSections, section => {
      const sectionId = _.get(section, "id");
      const configKey = ROW_SECTION_ID_TO_CONFIG_KEY[sectionId];
      const sectionConfig = _.get(transcriptConfiguration, configKey, {});
      const configuredDisplayOrder = _.get(sectionConfig, "displayOrder", []);

      const rawDisplayOrder = !_.isEmpty(configuredDisplayOrder)
        ? configuredDisplayOrder
        : _.map(_.get(section, "items", []), "id");

      const displayOrder = _.map(rawDisplayOrder, id => {
        const upperCaseId = _.toUpper(id);
        const metadataId =
          sectionId === "year_level_aggregates"
            ? getNormalizedYearAggregateItemId(upperCaseId)
            : upperCaseId;

        return {
          rawId: upperCaseId,
          metadataId,
        };
      });

      const sectionConfigMap = _.get(sectionConfig, "config", {});

      const items = _.map(displayOrder, ({ rawId, metadataId }) => {
        const rowConfig = _.get(sectionConfigMap, rawId, {});
        const rowItem = getRowItemMetaById({
          items: _.get(section, "items", []),
          itemId: metadataId,
        });
        const isGpa = metadataId === ROW_ITEM_KEYS.GPA;
        const enabledGradeScaleIds = getEnabledGradeScaleIds({
          itemId: rawId,
          rowConfig,
          gradeScaleTypeMap,
        });

        const shouldUseChildren =
          !_.includes(ROW_SECTION_IDS_WITHOUT_CHILDREN, sectionId) && !isGpa;

        const childTypes = shouldUseChildren
          ? getRowChildTypes({
              itemId: rawId,
              rowItem,
              enabledGradeScaleIds,
              gradeScaleTypeMap,
            })
          : [];

        const children = shouldUseChildren
          ? buildRowChildren({
              childTypes,
              enabledGradeScaleIds,
              gradeScaleTypeMap,
              gradeScaleLabelMap,
              getResolvedLabel: resolvedChildLabelFn,
              itemId: rawId,
            })
          : [];

        const parentCheckState = getParentCheckState({ children });

        const label = _.get(rowConfig, "label", { localizedKey: rawId });
        const description =
          sectionId === "year_level_aggregates"
            ? getYearAggregateDescription({
                itemId: rawId,
                transcriptConfiguration,
                t,
              })
            : getDescriptionByItemId({
                rowItem,
                t,
              });
        return {
          id: rawId,
          content:
            getLabelValue({ label, language, t }) ||
            _.startCase(_.toLower(rawId)),
          label,
          description,
          isEnabled: shouldUseChildren
            ? parentCheckState.isEnabled
            : _.get(rowConfig, "isEnabled", false),
          isIndeterminate: shouldUseChildren
            ? parentCheckState.isIndeterminate
            : false,
          isCheckable: true,
          isLabelEditable: true,
          children,
        };
      });

      return {
        sectionId,
        title:
          sectionId === "across_year_aggregates"
            ? t("transcript:display_settings_across_year_aggregates_heading")
            : t(`transcript:${sectionId}`),
        items,
        enableReorder: true,
        showSettings: false,
        showManageColumns: false,
      };
    });
  }, [
    configuredRowSections,
    language,
    t,
    transcriptConfiguration,
    createResolvedChildLabelFn,
  ]);

  return { rowSectionViewModel };
};
